import { useEffect, useState } from "react";
import { Volume2, X } from "lucide-react";

const KEY = "msc_audio_armed";

/**
 * Shows a one-time click-to-arm banner so the walk-up track and cha-ching
 * chime can play without hitting browser autoplay restrictions.
 *
 * Once armed (session-wide), the banner never shows again this session.
 */
export function AudioArmBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const armed = sessionStorage.getItem(KEY) === "1";
      if (!armed) setVisible(true);
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const arm = async () => {
    try {
      // Prime the AudioContext with a silent oscillator to unlock playback
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, ctx.currentTime);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.01);
        setTimeout(() => { try { ctx.close(); } catch (e) { /* ignore */ } }, 200);
      }
      // Also prime an <audio> element with a data URL so future playback works.
      const a = new Audio(
        "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAA="
      );
      a.volume = 0;
      try { await a.play(); } catch (e) { /* ignore */ }
      sessionStorage.setItem(KEY, "1");
    } catch (e) {
      // ignore
    }
    setVisible(false);
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(KEY, "1");
    } catch (e) {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[70] flex items-center gap-3 px-4 py-2.5 shadow-lg animate-fade-in"
      style={{
        background: "linear-gradient(90deg, rgba(34,211,192,0.15) 0%, rgba(14,19,21,0.98) 100%)",
        border: "1px solid rgba(34,211,192,0.4)",
        clipPath: "polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%)",
      }}
      data-testid="audio-arm-banner"
    >
      <Volume2 size={16} className="text-cyan shrink-0" />
      <div className="text-xs text-ink leading-tight max-w-[240px]">
        <div className="font-mono uppercase tracking-wider text-[10px] text-cyan">Arm sound</div>
        <div className="text-ink-secondary mt-0.5">
          Tap to unlock cha-ching + walk-up tracks for closes.
        </div>
      </div>
      <button
        onClick={arm}
        data-testid="audio-arm-btn"
        className="bg-cyan text-bg font-medium text-xs px-3 py-1.5 hover:bg-cyan-hover transition-colors"
        style={{ clipPath: "polygon(4px 0, 100% 0, calc(100% - 4px) 100%, 0 100%)" }}
      >
        ARM
      </button>
      <button
        onClick={dismiss}
        data-testid="audio-arm-dismiss"
        className="text-ink-muted hover:text-ink transition-colors"
        title="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
